/**
 * Enhanced Navigation - Phase 2 Implementation
 *
 * DEVELOPMENT LOG: Phase 2 - Enhanced Navigation & User Account
 * Created: Enhanced navigation with logo, user account, and mobile support
 * Status: ✅ Complete
 */

import React, { useEffect, useRef, useState, Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { DebugLabel } from '../shared';
import { isAdminUser } from '../../utils/adminAccess';
import { useFeedbackModal } from '../../contexts/FeedbackModalContext';
import { usePageControls } from '../../contexts/PageControlsContext';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { isFeatureEnabled } from '../../utils/featureFlags';
import { BackControlsDual } from '../results/shared/BackControlsDual';
import { ArrowPathIcon, DocumentTextIcon, TrophyIcon, ScaleIcon } from '@heroicons/react/24/outline';
import { useDevice } from '../../features/consensus/hooks/useDevice';
import { isFeatureEnabled as isFeatureEnabledLegacy } from '../../lib/flags';

interface User {
  name: string;
  email: string;
  avatar?: string;
  plan: 'free' | 'pro';
  planDisplayName?: string; // e.g. "Edge Plan", "Flow Plan"
}

interface EnhancedNavigationProps {
  user?: User | null;
  onSignIn?: () => void;
  onSignOut?: () => void;
}

export const EnhancedNavigation: React.FC<EnhancedNavigationProps> = ({
  user = null,
  onSignIn = () => console.log('Sign in clicked'),
  onSignOut = () => console.log('Sign out clicked')
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { enabled: feedbackEnabled, openModal: openFeedbackModal } = useFeedbackModal();
  const pageControls = usePageControls();
  const { isMobile } = useDevice();
  const anonymousFlagEnabled = useFeatureFlag('ANONYMOUS');
  const paywallEnabled = useFeatureFlag('PAYWALL');
  const betaEnabled = useFeatureFlag('BETA');
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);

  const hasAdminAccess = isAdminUser(user);
  const showPageControlsMenu = pageControls.showControls;
  const isAnonymous = !user;

  // Detect if we're in a comparison detail view (e.g., /results/[id])
  // Pattern: /results/ followed by a UUID or slug (not empty, not just "/results")
  const isComparisonDetailView = React.useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    // Check if path is /results/[something] where something is not empty
    return pathSegments.length === 2 && pathSegments[0] === 'results' && pathSegments[1].length > 0;
  }, [location.pathname]);

  // Handler for back button in comparison detail view
  const handleNavigateToAllComparisons = React.useCallback(() => {
    navigate('/results');
  }, [navigate]);

  // Use actual user or fallback to demo user if needed
  const demoUser = user || {
    name: 'Demo User',
    email: 'demo@smartprompt.ai',
    avatar: undefined,
    plan: 'free' as const
  };

  const isLoggedIn = user !== null; // Check if user is actually logged in

  const restrictedMobileOverflowPaths = [
    '/compare', '/results', '/dashboard', '/profile', '/processing',
    '/', '/profile-menu', '/settings', '/billing', '/legal'
  ];
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isRestrictedTopLevel = restrictedMobileOverflowPaths.some(path => path === normalizedPathname);
  const isNestedRestrictedRoute = normalizedPathname.startsWith('/compare/');
  // NEW_DESIGN_BETA graduated - features always enabled
  const hideMobileOverflowMenu = (isRestrictedTopLevel || isNestedRestrictedRoute);

  // Helper function to determine if a link is active
  const isActiveLink = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Get active segment index for animation (0=Compare, 1=Results, 2=Dashboard)
  const getActiveSegmentIndex = React.useMemo(() => {
    if (location.pathname.startsWith('/compare')) return 0;
    if (location.pathname.startsWith('/results')) return 1;
    if (location.pathname.startsWith('/dashboard')) return 2;
    return 0; // Default to Compare
  }, [location.pathname]);

  // Helper function to get link classes
  const getLinkClasses = (path: string) => {
    return isActiveLink(path)
      ? 'text-cyan-400 font-medium transition-colors'
      : 'text-slate-300 hover:text-white transition-colors';
  };

  // Helper function to determine navigation path for anonymous users
  // Priority: BETA > ANONYMOUS flag
  // - If BETA ON + anonymous → /beta (request form) or /login (if already submitted)
  // - If BETA OFF + ANONYMOUS disabled → /signup
  const getNavigationPath = (path: string): string => {
    const protectedRoutes = ['/compare', '/results', '/dashboard', '/profile', '/settings', '/billing'];

    if (isAnonymous && protectedRoutes.includes(path)) {
      console.log('[EnhancedNavigation] getNavigationPath', {
        path,
        betaEnabled,
        anonymousFlagEnabled,
        hasSubmittedRequest: localStorage.getItem('sp_beta_request_submitted') === 'true'
      });

      // BETA flag takes priority
      if (betaEnabled) {
        // Check if user already submitted beta request
        const hasSubmittedRequest = localStorage.getItem('sp_beta_request_submitted') === 'true';
        const redirectTo = hasSubmittedRequest ? '/login' : '/beta';
        console.log('[EnhancedNavigation] BETA enabled, redirecting to:', redirectTo);
        return redirectTo;
      }

      // Fall back to ANONYMOUS flag behavior
      if (!anonymousFlagEnabled) {
        console.log('[EnhancedNavigation] ANONYMOUS disabled, redirecting to /signup');
        return '/signup';
      }
    }

    return path;
  };

  // Calculate and set header height as CSS variable
  useEffect(() => {
    if (headerRef.current) {
      const updateHeight = () => {
        const height = headerRef.current!.offsetHeight;
        document.documentElement.style.setProperty('--main-nav-height', `${height}px`);
      };

      // Initial calculation
      updateHeight();

      // Update on resize
      window.addEventListener('resize', updateHeight);

      // Update on orientation change (mobile)
      window.addEventListener('orientationchange', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
        window.removeEventListener('orientationchange', updateHeight);
      };
    }
  }, []);

  // Close user menu when clicking outside of the trigger or menu
  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      if (userMenuRef.current?.contains(target)) return;
      if (userMenuButtonRef.current?.contains(target)) return;

      setIsUserMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  return (
    <header ref={headerRef} className="sticky top-0 bg-slate-900/50 backdrop-blur-sm border-b border-slate-700/50 z-[9999]" data-testid="main-navigation">
      <DebugLabel label="EnhancedNavigation-Header" type="layout" position="top-right" />

      <div className="max-w-6xl mx-auto px-6 py-2 sm:py-4">
        <div className="flex justify-between items-center gap-3">

          {/* Logo and Brand OR Back Button (for comparison detail view) */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {pageControls.navSlot ? (
              <>
                <div className="md:hidden">{pageControls.navSlot}</div>
                <div className="hidden md:flex items-center space-x-3">
                  <Link to="/" className="flex items-center space-x-3">
                    <img
                      src="/logos/Smart Prompt Logo.png"
                      alt="Smart Prompt"
                      className="w-10 h-10 object-contain"
                      onError={(e) => {
                        // Fallback to gradient logo if image fails
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg items-center justify-center hidden">
                      <span className="text-white font-bold text-sm">SP</span>
                    </div>
                    <span className="hidden md:inline text-xl font-semibold text-white">Smart Prompt</span>
                  </Link>
                </div>
              </>
            ) : isComparisonDetailView ? (
              <>
                {/* Show back button on mobile only in comparison detail view */}
                <div className="md:hidden">
                  <BackControlsDual
                    onNavigateToAll={handleNavigateToAllComparisons}
                    showParentButton={false}
                    useAllButton={false}
                  />
                </div>

                {/* Show logo on desktop in comparison detail view */}
                <div className="hidden md:flex items-center space-x-3">
                  <Link to="/" className="flex items-center space-x-3">
                    <img
                      src="/logos/Smart Prompt Logo.png"
                      alt="Smart Prompt"
                      className="w-10 h-10 object-contain"
                      onError={(e) => {
                        // Fallback to gradient logo if image fails
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg items-center justify-center hidden">
                      <span className="text-white font-bold text-sm">SP</span>
                    </div>
                    <span className="hidden md:inline text-xl font-semibold text-white">Smart Prompt</span>
                  </Link>
                </div>
              </>
            ) : (
              // Show normal logo when not in comparison detail view
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="/logos/Smart Prompt Logo.png"
                  alt="Smart Prompt"
                  className="w-10 h-10 object-contain"
                  onError={(e) => {
                    // Fallback to gradient logo if image fails
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg items-center justify-center hidden">
                  <span className="text-white font-bold text-sm">SP</span>
                </div>
                <span className="hidden md:inline text-xl font-semibold text-white">Smart Prompt</span>
              </Link>
            )}
          </div>

          {/* Mobile Center Slot - Tab Navigation for Comparison Detail (hidden for Response Detail) */}
          {isMobile && pageControls.comparisonDetailView !== undefined && pageControls.onComparisonDetailViewChange && isFeatureEnabledLegacy('FF_RESULTS_CONSENSUS') && (pageControls.comparisonResponsesCount ?? 0) > 1 && !pageControls.isResponseDetail && (
            <div className="flex items-center justify-center gap-2 flex-1">
              {/* Responses Tab */}
              <button
                onClick={() => pageControls.onComparisonDetailViewChange?.('outputs')}
                aria-label="Responses result type"
                className={`w-10 h-10 p-2 rounded-lg transition-all duration-200 flex items-center justify-center ${
                  pageControls.comparisonDetailView === 'outputs'
                    ? 'bg-cyan-500/20 border border-cyan-400/50 text-cyan-300'
                    : 'bg-slate-700/30 border border-slate-600/50 text-slate-400 hover:bg-slate-700/50 hover:text-white'
                }`}
              >
                <DocumentTextIcon className="w-6 h-6" />
              </button>

              {/* Scores Tab - Locked for anonymous users */}
              <button
                onClick={() => {
                  if (!user) return; // Prevent navigation for anonymous users
                  pageControls.onComparisonDetailViewChange?.('scoring');
                }}
                aria-label={!user ? "Scores (Sign up required)" : "Scores result type"}
                disabled={!user}
                className={`w-10 h-10 p-2 rounded-lg transition-all duration-200 flex items-center justify-center ${
                  !user
                    ? 'bg-slate-800/30 border border-slate-700/50 text-slate-500 cursor-not-allowed opacity-60'
                    : pageControls.comparisonDetailView === 'scoring'
                    ? 'bg-cyan-500/20 border border-cyan-400/50 text-cyan-300'
                    : 'bg-slate-700/30 border border-slate-600/50 text-slate-400 hover:bg-slate-700/50 hover:text-white'
                }`}
              >
                <TrophyIcon className="w-6 h-6" />
              </button>

              {/* Consensus Tab - Locked for anonymous users */}
              <button
                onClick={() => {
                  if (!user) return; // Prevent navigation for anonymous users
                  pageControls.onComparisonDetailViewChange?.('consensus');
                }}
                aria-label={!user ? "Consensus (Sign up required)" : "Consensus result type"}
                disabled={!user}
                className={`w-10 h-10 p-2 rounded-lg transition-all duration-200 flex items-center justify-center ${
                  !user
                    ? 'bg-slate-800/30 border border-slate-700/50 text-slate-500 cursor-not-allowed opacity-60'
                    : pageControls.comparisonDetailView === 'consensus'
                    ? 'bg-cyan-500/20 border border-cyan-400/50 text-cyan-300'
                    : 'bg-slate-700/30 border border-slate-600/50 text-slate-400 hover:bg-slate-700/50 hover:text-white'
                }`}
              >
                <ScaleIcon className="w-6 h-6" />
              </button>
            </div>
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {!isLoggedIn && (
              <Link
                to="/"
                className={getLinkClasses('/')}
              >
                Home
              </Link>
            )}

            {/* Segmented Control for Compare/Results/Dashboard */}
            <div className="relative w-[420px] max-w-[45vw] rounded-full bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 p-1 shadow-inner">
              <nav
                aria-label="Primary navigation"
                className="relative flex items-center gap-0"
              >
                {/* Animated sliding pill background */}
                <div
                  className="absolute inset-y-0 left-0 w-1/3 bg-cyan-500/20 border border-cyan-400/50 rounded-full shadow-lg transition-all duration-500 ease-in-out"
                  style={{
                    transform: `translateX(${getActiveSegmentIndex * 100}%)`
                  }}
                  aria-hidden="true"
                />

                {/* Compare Segment */}
                <Link
                  to={getNavigationPath('/compare')}
                  className={`relative z-10 flex-1 rounded-full px-3 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300 ${
                    isActiveLink('/compare')
                      ? 'text-cyan-300'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  aria-current={isActiveLink('/compare') ? 'page' : undefined}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  <span>Run</span>
                </Link>

                {/* Results Segment */}
                <Link
                  to={getNavigationPath('/results')}
                  className={`relative z-10 flex-1 rounded-full px-3 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300 ${
                    isActiveLink('/results')
                      ? 'text-cyan-300'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  aria-current={isActiveLink('/results') ? 'page' : undefined}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 12a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1v-7z"
                    />
                  </svg>
                  <span>Results</span>
                </Link>

                {/* Dashboard Segment */}
                <Link
                  to={getNavigationPath('/dashboard')}
                  className={`relative z-10 flex-1 rounded-full px-3 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-300 ${
                    isActiveLink('/dashboard')
                      ? 'text-cyan-300'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  aria-current={isActiveLink('/dashboard') ? 'page' : undefined}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span>Insights</span>
                </Link>
              </nav>
            </div>

            {/* BETA Feedback Button */}
            {feedbackEnabled && (
              <button
                onClick={openFeedbackModal}
                className="px-2.5 py-1 rounded-full border border-cyan-500/60 bg-slate-900/70 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-300 hover:border-cyan-400/80 hover:bg-slate-800/80 transition-colors"
              >
                BETA
              </button>
            )}

            {/* User Account Section */}
            {isLoggedIn ? (
              <div className="relative" data-testid="user-menu" ref={userMenuRef}>
                <button
                  ref={userMenuButtonRef}
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-800/50 transition-colors"
                >
                  {demoUser.avatar ? (
                    <img
                      src={demoUser.avatar}
                      alt={demoUser.name}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {demoUser.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                  <div className="text-left">
                    <div className="text-sm font-medium text-white">{demoUser.name}</div>
                    <div className="text-xs text-slate-400">{demoUser.planDisplayName || (demoUser.plan === 'pro' ? 'Pro Plan' : 'Free Plan')}</div>
                  </div>
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-[9999]">
                    <div className="py-1">
                      <div className="px-4 py-2 text-xs text-slate-400 border-b border-slate-700">
                        {demoUser.email}
                      </div>
                      <Link
                        to={getNavigationPath('/profile')}
                        className="block px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Settings
                      </Link>
                      {paywallEnabled && (
                        <Link
                          to="/billing"
                          className="block px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Billing
                        </Link>
                      )}
                      <Link
                        to="/legal"
                        className="block px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Legal
                      </Link>

                      {/* Admin Section - Only show if user has admin access */}
                      {hasAdminAccess && (
                        <div className="border-t border-slate-700 mt-1">
                          <Link
                            to="/admin"
                            className="w-full flex items-center px-4 py-2 text-sm text-cyan-400 hover:bg-slate-700 hover:text-cyan-300"
                            onClick={() => setIsUserMenuOpen(false)}
                            data-testid="admin-feature-flags-link"
                          >
                            <span>🔧 Admin → Dashboard</span>
                          </Link>
                        </div>
                      )}

                      <div className="border-t border-slate-700">
                        <button
                          onClick={() => {
                            setIsUserMenuOpen(false);
                            onSignOut();
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                        >
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={onSignIn}
                className="text-slate-300 hover:text-white transition-colors md:text-cyan-100 md:font-semibold md:px-3 md:py-1.5 md:rounded-lg md:border md:border-cyan-400/60 md:bg-cyan-400/10 md:hover:bg-cyan-400/20 md:shadow-[0_0_0_1px_rgba(34,211,238,0.25)] md:focus-visible:outline md:focus-visible:outline-2 md:focus-visible:outline-offset-2 md:focus-visible:outline-cyan-300"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Actions */}
          <div className="md:hidden flex items-center gap-2 flex-shrink-0">
            {feedbackEnabled && isLoggedIn && (
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  openFeedbackModal();
                }}
                className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] rounded-full bg-cyan-500 text-slate-900 shadow-sm hover:bg-cyan-400 transition-colors"
              >
                BETA
              </button>
            )}
            {/* Show ellipsis menu for page controls OR normal navigation menu */}
            {(showPageControlsMenu || !hideMobileOverflowMenu) && (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-300 hover:text-white p-2"
                data-testid="mobile-menu-button"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            {showPageControlsMenu ? (
              // COMPACT ACTIONS MENU (Comparison Detail / Response Detail View) - Headless UI Dialog
              <Dialog
                open={isMobileMenuOpen}
                onClose={() => setIsMobileMenuOpen(false)}
                className="relative z-[100] md:hidden"
              >
                {/* Backdrop with fade animation */}
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <DialogBackdrop className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
                </Transition.Child>

                {/* Actions container aligned near the top for detail views */}
                <div className="fixed inset-0 flex items-start justify-center p-4 pt-16">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-full scale-95"
                    enterTo="opacity-100 translate-y-0 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 scale-100"
                    leaveTo="opacity-0 translate-y-8 scale-95"
                  >
                    <DialogPanel
                      className="w-full max-w-sm bg-slate-900/95 backdrop-blur-xl border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden"
                      data-testid="mobile-actions-menu"
                    >
                      {/* Accessibility title (visually hidden) */}
                      <DialogTitle className="sr-only">Actions Menu</DialogTitle>

                      {/* Header with explicit close affordance */}
                      <div className="flex justify-end px-4 pt-3 pb-2">
                        <button
                          type="button"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="p-2 text-slate-300 hover:text-white rounded-full hover:bg-slate-800/70 focus:outline-none focus-visible:ring focus-visible:ring-cyan-300"
                          aria-label="Close actions menu"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      {/* Menu buttons */}
                      <div className="px-3 pb-3 space-y-2">
                        {/* Favorite */}
                        <button
                          onClick={() => {
                            pageControls.onFavorite?.();
                            setIsMobileMenuOpen(false);
                          }}
                          className="flex items-center gap-3 w-full px-4 py-3 text-sm rounded-xl transition-colors text-slate-100 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 active:scale-[0.98]"
                        >
                          <svg
                            className={`w-5 h-5 flex-shrink-0 ${
                              pageControls.isFavorited ? 'text-yellow-300 fill-yellow-300' : 'text-slate-200'
                            }`}
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                            fill={pageControls.isFavorited ? 'currentColor' : 'none'}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                            />
                          </svg>
                          <span className="font-semibold">Favorite</span>
                        </button>

                        {/* Copy */}
                        <button
                          onClick={() => {
                            pageControls.onCopy?.();
                            setIsMobileMenuOpen(false);
                          }}
                          className="flex items-center gap-3 w-full px-4 py-3 text-sm rounded-xl transition-colors text-slate-100 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 active:scale-[0.98]"
                        >
                          <svg className="w-5 h-5 flex-shrink-0 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2" />
                            <rect x="10" y="8" width="10" height="12" rx="2" ry="2" />
                          </svg>
                          <span className="font-semibold">Copy</span>
                        </button>

                        {/* Reprompt */}
                        <button
                          onClick={() => {
                            pageControls.onReprompt?.();
                            setIsMobileMenuOpen(false);
                          }}
                          className="flex items-center gap-3 w-full px-4 py-3 text-sm rounded-xl transition-colors text-slate-100 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 active:scale-[0.98]"
                        >
                          <ArrowPathIcon className="w-5 h-5 flex-shrink-0 text-slate-200" />
                          <span className="font-semibold">Reprompt</span>
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => {
                            pageControls.onDelete?.();
                            setIsMobileMenuOpen(false);
                          }}
                          className="flex items-center gap-3 w-full px-4 py-3 text-sm rounded-xl transition-colors text-red-100 bg-red-900/50 border border-red-700/70 shadow-sm hover:bg-red-800/60 active:scale-[0.98]"
                        >
                          <svg className="w-5 h-5 flex-shrink-0 text-red-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m-3 0h14" />
                          </svg>
                          <span className="font-semibold">Delete</span>
                        </button>
                      </div>
                    </DialogPanel>
                  </Transition.Child>
                </div>
              </Dialog>
            ) : (
              // NORMAL NAVIGATION MENU (everywhere else)
              <div
                className="md:hidden fixed inset-0 z-[10000]"
                data-testid="mobile-menu"
                role="dialog"
                aria-modal="true"
              >
                <div
                  className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setIsUserMenuOpen(false);
                  }}
                />

                <div className="relative h-full w-full pointer-events-none">
                  <div className="absolute inset-x-4 top-20">
                    <div
                      className="bg-slate-900/95 border border-slate-700 rounded-2xl shadow-2xl p-4 max-h-[70vh] overflow-y-auto pointer-events-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <h3 className="text-slate-200 text-sm font-semibold tracking-wide">Menu</h3>
                        <span className="text-xs text-slate-400">Mobile</span>
                      </div>

                      <div className="space-y-3">
                        <Link
                          to="/"
                          className={`block rounded-xl px-3 py-2 bg-slate-800/80 border border-slate-700/70 shadow-sm transition-colors ${getLinkClasses('/')}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Home
                        </Link>
                        <Link
                          to={getNavigationPath('/compare')}
                          className={`block rounded-xl px-3 py-2 bg-slate-800/80 border border-slate-700/70 shadow-sm transition-colors ${getLinkClasses('/compare')}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Run
                        </Link>
                        <Link
                          to={getNavigationPath('/results')}
                          className={`block rounded-xl px-3 py-2 bg-slate-800/80 border border-slate-700/70 shadow-sm transition-colors ${getLinkClasses('/results')}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Results
                        </Link>
                        <Link
                          to={getNavigationPath('/dashboard')}
                          className={`block rounded-xl px-3 py-2 bg-slate-800/80 border border-slate-700/70 shadow-sm transition-colors ${getLinkClasses('/dashboard')}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Insights
                        </Link>
                      </div>

                      {isLoggedIn ? (
                        <div className="space-y-3 border-t border-slate-700/60 pt-4">
                          <div className="flex items-center space-x-3">
                            {demoUser.avatar ? (
                              <img
                                src={demoUser.avatar}
                                alt={demoUser.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {demoUser.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-semibold text-white">{demoUser.name}</div>
                              <div className="text-xs text-slate-400">{demoUser.plan === 'pro' ? 'Pro Plan' : 'Free Plan'}</div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Link
                              to={getNavigationPath('/profile')}
                              className="block rounded-xl px-3 py-2 text-slate-200 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              Profile
                            </Link>
                            <Link
                              to="/settings"
                              className="block rounded-xl px-3 py-2 text-slate-200 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              Settings
                            </Link>
                            {paywallEnabled && (
                              <Link
                                to="/billing"
                                className="block rounded-xl px-3 py-2 text-slate-200 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                Billing
                              </Link>
                            )}
                            <Link
                              to="/legal"
                              className="block rounded-xl px-3 py-2 text-slate-200 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 transition-colors"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              Legal
                            </Link>
                            <button
                              onClick={() => {
                                setIsMobileMenuOpen(false);
                                onSignOut();
                              }}
                              className="block w-full text-left rounded-xl px-3 py-2 text-slate-200 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 transition-colors"
                            >
                              Sign Out
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            onSignIn();
                          }}
                          className="block w-full text-left rounded-xl px-3 py-2 text-slate-200 bg-slate-800/80 border border-slate-700/70 shadow-sm hover:bg-slate-700/80 transition-colors"
                        >
                          Sign In
                        </button>
                      )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Click outside to close desktop user menu */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => {
            setIsUserMenuOpen(false);
          }}
        />
      )}
    </header>
  );
};
